import React, { useState, useEffect } from 'react';
import { storage } from '../utils/localStorage';

interface AuthenticatedImageProps {
  url: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Image component that loads images from authenticated URLs (with Bearer token)
 */
export const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({
  url,
  alt = 'Image',
  className = '',
  style = {}
}) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl = '';
    let isCancelled = false;

    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const loadImageWithRetry = async (retryCount = 0): Promise<void> => {
      if (isCancelled) return;

      try {
        const token = storage.getItem('token');

        const fetchOptions: RequestInit = {};
        if (token) {
          fetchOptions.headers = {
            'Authorization': `Bearer ${token}`
          };
        }

        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
          // If 404 and we haven't exhausted retries, try again with exponential backoff
          if (response.status === 404 && retryCount < 3) {
            const delayMs = Math.pow(2, retryCount) * 500; // 500ms, 1s, 2s
            await sleep(delayMs);
            return loadImageWithRetry(retryCount + 1);
          }
          throw new Error(`Failed to load image: ${response.status}`);
        }

        if (isCancelled) return;

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
        setLoading(false);
      } catch (err) {
        if (!isCancelled) {
          console.error('Error loading image:', err);
          setError(true);
          setLoading(false);
        }
      }
    };

    const loadImage = async () => {
      setLoading(true);
      setError(false);
      await loadImageWithRetry(0);
    };

    loadImage();

    // Cleanup: revoke object URL when component unmounts or URL changes
    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  if (loading) {
    return (
      <div className={`d-flex align-items-center justify-content-center ${className}`} style={{ ...style, minHeight: '100px', backgroundColor: '#f0f0f0' }}>
        <span className="text-muted">Načítavam...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`d-flex align-items-center justify-content-center ${className}`} style={{ ...style, minHeight: '100px', backgroundColor: '#f0f0f0' }}>
        <span className="text-danger">Chyba pri načítaní obrázka</span>
      </div>
    );
  }

  return <img src={imageSrc} alt={alt} className={className} style={style} />;
};
